import useAxios from '../hooks/useAxios';
import { NURALOGIX_TOKEN_URL, NURALOGIX_USERID_URL, NURALOGIX_RESULTS_VITALS_URL, NURALOGIX_URL_ANON } from './constants';

/**
 * Hook for interacting with the Nuralogix API.
 *
 * @returns {Object} An object containing functions to interact with the API.
 */
export const useNuralogixApi = () => {
  const api = useAxios();

  /**
   * Posts a Nuralogix token for a specific assessment.
   *
   * @param {string} dataOS - The operating system data.
   * @param {string} assessmentId - The ID of the assessment.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const postNuralogixToken = ( dataOS, assessmentId ) =>
    api.post( NURALOGIX_TOKEN_URL, {
      device_type: dataOS,
      assessment_id: assessmentId,
      redirect_url: '/'
    } );

  /**
   * Gets the Nuralogix user ID.
   *
   * @returns {Promise} A Promise that resolves with the Nuralogix user ID.
   */
  const getNuralogixUserId = () => api.get( NURALOGIX_USERID_URL );

  /**
   * Gets the Nuralogix results for vitals based on an assessment ID.
   *
   * @param {string} assessmentId - The ID of the assessment.
   * @returns {Promise} A Promise that resolves with the Nuralogix vitals results.
   */
  const getNuralogixResultsVitals = assessmentId => api.get( `${ NURALOGIX_RESULTS_VITALS_URL }/${ assessmentId }` );

  /**
   * Posts Nuralogix vitals results for a specific assessment.
   *
   * @param {string} assessmentId - The ID of the assessment.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const postNuralogixResultsVitals = assessmentId =>
    api.post( NURALOGIX_RESULTS_VITALS_URL, {
      assessment_id: assessmentId,
    } );

  const postNuralogixUrlAnon = deviceType => api.post( `${ NURALOGIX_URL_ANON }?device_type=${ deviceType }` );

  return {
    postNuralogixToken,
    getNuralogixUserId,
    getNuralogixResultsVitals,
    postNuralogixResultsVitals,
    postNuralogixUrlAnon,
  };
};
